// src/views/Cart/Cart.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import "./Cart.css";
import { Link } from "react-router-dom";
import NotImageIcon from "../../assets/error.png";
import CartBottomMenu from "../../components/CartBottomMenu/CartBottomMenu";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import { useSEO } from "../../context/SEOContext";
import { useTranslation } from "react-i18next";
import { localizeCartItems } from "../../utils/productLoader";

// 🔸 ortak fiyat helper
import { pickDisplayPrice, formatMoney } from "../../utils/pricePicker";

const Cart = () => {
  const { t, i18n } = useTranslation();
  const setSeo = useSEO();

  useEffect(() => {
    setSeo({
      title: t("cart.meta.title"),
      description: t("cart.meta.description"),
      keywords: t("cart.meta.keywords"),
      favicon: "/faviconnew.png",
    });
  }, [setSeo, t]);

  const [cartItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const { currency } = useCurrency();

  // 🔸 sadece currency, conversionRate artık kullanılmıyor

  const pricingRegion = useMemo(() => {
    const explicit = localStorage.getItem("pricingRegion");
    if (explicit === "tr" || explicit === "intl") return explicit;

    // elinde ülke bilgisi varsa buraya bağla (örnek key’ler)
    const s = (
      localStorage.getItem("selectedCountry") ||
      localStorage.getItem("shipping_country") ||
      localStorage.getItem("country") ||
      ""
    ).toLowerCase();

    if (/(^|[^a-z])(tr|turkey|türkiye)([^a-z]|$)/i.test(s)) return "tr";

    // ✅ fallback: TRY ise TR fiyatı kullan
    if (currency === "TRY") return "tr";

    // opsiyonel: dil tr ise TR
    if ((i18n.language || "").toLowerCase().startsWith("tr")) return "tr";

    return "intl";
  }, [currency, i18n.language]);

  // ✅ aynı satır eşleştirme (customization dahil ayrı satır olsun diye variantKey kullan)
  const isSameCartLine = (a, b) => {
    if (a?.variantKey && b?.variantKey) return a.variantKey === b.variantKey;

    // fallback (eski kayıtlar)
    const aVar = a?.selectedVariation?.name || "Default";
    const bVar = b?.selectedVariation?.name || "Default";
    return a?.id === b?.id && aVar === bVar;
  };

  const getLocalizedColorName = (item, t) => {
    const selected = item.selectedVariation?.name;
    const defaultColor = item.attributes?.color?.split(",")[0]?.trim();
    const rawColor =
      selected && selected !== "Default" ? selected.trim() : defaultColor || "";
    if (!rawColor) return "";
    const normalized =
      rawColor.charAt(0).toUpperCase() + rawColor.slice(1).toLowerCase();
    return t(normalized);
  };

  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(storedCart);

    const offcanvasEl = document.getElementById("offcanvasRight");
    const handleOffcanvasClose = () => setSelectedProduct(null);
    if (offcanvasEl) {
      offcanvasEl.addEventListener("hidden.bs.offcanvas", handleOffcanvasClose);
    }

    const handleCartUpdated = () => {
      const updatedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(updatedCart);
    };
    window.addEventListener("cartUpdated", handleCartUpdated);

    return () => {
      if (offcanvasEl) {
        offcanvasEl.removeEventListener(
          "hidden.bs.offcanvas",
          handleOffcanvasClose,
        );
      }
      window.removeEventListener("cartUpdated", handleCartUpdated);
    };
  }, []);

  const getColorImage = (item) => {
    try {
      const selectedColor = item.selectedVariation?.name?.toLowerCase().trim();
      const colorFromAttributes = item.attributes.color
        ? item.attributes.color
            .split(",")
            .map((color) => color.toLowerCase().trim())[0]
        : null;
      const colorToMatch = selectedColor || colorFromAttributes;

      if (colorToMatch && item.color_images && item.color_images.length > 0) {
        const matching = item.color_images.find(
          (c) => c.name.toLowerCase().trim() === colorToMatch,
        );
        if (matching && matching.images.length > 0) {
          return matching.images[0];
        }
      }
      if (item.default_images && item.default_images.length > 0) {
        return item.default_images[0];
      }
      return "";
    } catch (e) {
      console.error("getColorImage error:", e);
      return "";
    }
  };

  const removeItem = (itemToRemove) => {
    const updatedCart = cartItems.filter(
      (it) => !isSameCartLine(it, itemToRemove),
    );
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));

    Swal.fire({
      icon: "error",
      title: "Product removed from cart",
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 500,
    });
  };

  const updateQuantity = (item, newQuantity) => {
    const updatedCart = cartItems.map((it) =>
      isSameCartLine(it, item) ? { ...it, quantity: newQuantity } : it,
    );
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  // Perfume ise manual_prices[currency] öncelikli, yoksa mevcut akış
  const isPerfumeItem = useCallback((obj) => {
    const c = obj?.category;
    if (Array.isArray(c)) return c.includes("Perfume");
    return typeof c === "string" && c.toLowerCase().includes("perfume");
  }, []);

  // Perfume ise manual_prices[currency] öncelikli, yoksa mevcut akış
  const pickCartPrice = useCallback(
    (item, curr) => {
      if (!item) return null;

      // 1) Perfume manual_prices önceliği (kalsın)
      if (isPerfumeItem(item) && item.manual_prices) {
        const val = item.manual_prices?.[curr];
        const n = Number(val);
        if (val != null && !Number.isNaN(n)) {
          return { amount: n, curr };
        }
      }

      // 2) ✅ Sepete varyasyon için kaydettiğin displayPrices varsa onu kullan
      if (item.displayPrices) {
        return pickDisplayPrice({ displayPrices: item.displayPrices }, curr);
      }

      // 3) ✅ Eski kayıtlar için fallback: seçili varyasyon adıyla çöz (varsa)
      const variantName =
        item.selectedVariation?.name &&
        item.selectedVariation.name !== "Default"
          ? item.selectedVariation.name
          : null;

      return pickDisplayPrice(item, curr, variantName);
    },
    [isPerfumeItem],
  );

  // ✅ Customization ek ücret (enabled ise) - item.customizationDisplayPrices içinden seçer
  const pickCustomizationPrice = useCallback(
    (item, curr) => {
      if (!item?.customization?.enabled) return null;

      const dp =
        item?.customizationDisplayPrices || item?.customization?.displayPrices;
      if (!dp) return null;

      const regionPrices = dp?.[pricingRegion]; // ✅ sadece tr / intl
      if (!regionPrices) return null;

      const val = regionPrices?.[curr];
      const n = Number(val);
      if (val == null || Number.isNaN(n)) return null;

      return { amount: n, curr };
    },
    [pricingRegion],
  );

  // ✅ sadece ürün (base) birim fiyatı
  const getBaseUnitPriceObj = useCallback(
    (item) => pickCartPrice(item, currency),
    [pickCartPrice, currency],
  );

  // ✅ sadece ürün (base) satır toplamı
  const getBaseLineTotalObj = useCallback(
    (item) => {
      const p = getBaseUnitPriceObj(item);
      const qty = item.quantity || 1;
      return p ? { amount: p.amount * qty, curr: p.curr } : null;
    },
    [getBaseUnitPriceObj],
  );

  const getCustomizationTotalObj = useMemo(() => {
    let sum = 0;
    let useCurr = currency;

    cartItems.forEach((it) => {
      if (!it?.customization?.enabled) return;

      const extra = pickCustomizationPrice(it, currency); // {amount,curr} | null
      if (extra) {
        sum += extra.amount * (it.quantity || 1);
        useCurr = extra.curr || useCurr;
      }
    });

    return { amount: sum, curr: useCurr };
  }, [cartItems, currency, pickCustomizationPrice]);

  // 🔸 Sepet toplamı (tek para birimi gibi davranır; helper çoğu durumda seçilen currency'i verir)
  const getSubtotalObj = useMemo(() => {
    let sum = 0;
    let useCurr = currency;

    cartItems.forEach((it) => {
      const line = getBaseLineTotalObj(it);
      if (line) {
        sum += line.amount;
        useCurr = line.curr || useCurr;
      }
    });

    return { amount: sum, curr: useCurr };
  }, [cartItems, currency, getBaseLineTotalObj]);
  // 🔸 Satır toplamı
  const getTotalObj = useMemo(() => {
    const subtotal = getSubtotalObj.amount || 0;
    const customization = getCustomizationTotalObj.amount || 0;
    const shipping = 0;

    return {
      amount: subtotal + customization + shipping,
      curr: getSubtotalObj.curr,
    };
  }, [getSubtotalObj, getCustomizationTotalObj]);

  useEffect(() => {
    const handleLanguageChange = () => window.location.reload();
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  useEffect(() => {
    const fetchLocalizedCart = async () => {
      const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
      const localizedCart = await localizeCartItems(storedCart, i18n.language);

      // ✅ localStorage snapshot alanlarını (category dahil) geri bas
      const byKey = new Map(
        storedCart.map((it) => [it?.variantKey, it]).filter(([k]) => !!k),
      );

      const merged = localizedCart.map((it) => {
        const src = it?.variantKey ? byKey.get(it.variantKey) : null;
        if (!src) return it;

        return {
          ...it,

          // ✅ cart snapshot alanları
          variantKey: src.variantKey,
          selectedVariation: src.selectedVariation || it.selectedVariation,
          displayPrices: src.displayPrices || it.displayPrices,
          customization: src.customization || it.customization,
          customizationDisplayPrices:
            src.customizationDisplayPrices || it.customizationDisplayPrices,

          // ✅ category snapshot
          category: src.category ?? it.category,
        };
      });

      setCartItems(merged);
    };

    fetchLocalizedCart();

    const syncWishlist = () => {
      const stored = JSON.parse(localStorage.getItem("wishlistItems")) || [];
      setWishlistItems(stored);
    };
    syncWishlist();
    window.addEventListener("storage", syncWishlist);
    window.addEventListener("wishlistUpdated", syncWishlist);

    return () => {
      window.removeEventListener("storage", syncWishlist);
      window.removeEventListener("wishlistUpdated", syncWishlist);
    };
  }, [i18n]);

  const handleWishlistClick = (productId) => (e) => {
    e.preventDefault();
    if (wishlistItems.includes(productId)) {
      const updated = wishlistItems.filter((id) => id !== productId);
      setWishlistItems(updated);
      localStorage.setItem("wishlistItems", JSON.stringify(updated));
      Swal.fire({
        icon: "success",
        title: t("RemovedWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
        background: "#fff",
        iconColor: "#dc3545",
      });
    } else {
      const updated = [...wishlistItems, productId];
      setWishlistItems(updated);
      localStorage.setItem("wishlistItems", JSON.stringify(updated));
      Swal.fire({
        icon: "success",
        title: t("AddWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
      });
    }
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("wishlistItems")) || [];
    setWishlistItems(saved);
  }, []);

  // ✅ category etiketini normalize etme fonksiyonu
  const getCategoryLabel = (item) => {
    const c = item?.category;
    if (Array.isArray(c)) return (c[0] || "").trim();
    if (typeof c === "string") return c.trim();
    return "";
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-7 bg-light p-5">
            {cartItems.length > 0 ? (
              <>
                <div className="row py-2">
                  <div className="col-lg-6">
                    <h4 className="fw-normal text-start" dir="rtl">
                      {t("MyShoppingCart")} (
                      {cartItems.reduce(
                        (total, item) => total + (item.quantity || 1),
                        0,
                      )}
                      )
                    </h4>
                  </div>
                  <div className="col-lg-6 text-end">
                    <a
                      href="/shop"
                      className="btn fw-14 text-decoration-underline"
                    >
                      {t("ContinueShopping")}
                    </a>
                  </div>
                </div>

                {cartItems.map((item, index) => {
                  const line = getBaseLineTotalObj(item); // {amount,curr} | null
                  return (
                    <div className="bg-white p-3 mb-3" key={index}>
                      <div className="row">
                        <div className="col-lg-6 border-end">
                          <img
                            src={getColorImage(item)}
                            alt={item.name}
                            className="w-100"
                          />
                        </div>
                        <div className="col-lg-6 px-0 d-flex flex-column">
                          <div className="border-bottom p-3">
                            <div className="d-flex justify-content-between mb-1">
                              <p className="mb-0">
                                <span className="fs-10 fw-normal">
                                  {item.stock_code}
                                </span>
                                <br />
                                <span className="mb-0 fs-4 fw-normal">
                                  {item.name}
                                </span>
                              </p>
                              <p className="mt-2">
                                <button
                                  className="btn"
                                  type="button"
                                  data-bs-toggle="offcanvas"
                                  data-bs-target="#offcanvasRight"
                                  aria-controls="offcanvasRight"
                                  onClick={() => setSelectedProduct(item)}
                                >
                                  <i className="fa-solid fa-chevron-right"></i>
                                </button>
                              </p>
                            </div>
                          </div>

                          <div className="p-3">
                            <div className="d-flex justify-content-between mb-1">
                              <p className="mb-0">{t("Color")}</p>
                              <p className="mb-0">
                                {getLocalizedColorName(item, t)}
                              </p>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                              <p className="mb-0">{t("Material")}</p>
                              <p className="mb-0">{item.attributes.material}</p>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                              <p className="mb-0">{t("Brand")}</p>
                              <p className="mb-0">{item.attributes.brand}</p>
                            </div>
                            {getCategoryLabel(item) && (
                              <div className="d-flex justify-content-between mb-1">
                                <p className="mb-0">{t("Category")}</p>
                                <p className="mb-0">{getCategoryLabel(item)}</p>
                              </div>
                            )}
                            {item?.customization?.enabled && (
                              <div className="d-flex justify-content-between mb-1">
                                <p className="mb-0">
                                  ✍️ {t("customization.label")}
                                </p>
                                <p className="mb-0">
                                  {t("customization.yes")}
                                  {item.customization?.text
                                    ? ` | ${item.customization.text}`
                                    : ""}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="mt-auto">
                            <div className="border-bottom p-3">
                              <div className="d-flex justify-content-between mb-1">
                                <select
                                  className="form-select p-3 pb-2 pt-2 w-25"
                                  value={item.quantity || 1}
                                  onChange={(e) =>
                                    updateQuantity(item, Number(e.target.value))
                                  }
                                >
                                  {[1, 2, 3].map((n) => (
                                    <option key={n} value={n}>
                                      {n}
                                    </option>
                                  ))}
                                </select>
                                <h5 className="mb-0 mt-2 fw-normal">
                                  {line
                                    ? formatMoney(line.amount, line.curr, {
                                        min: 0,
                                        max: 0,
                                      })
                                    : "-"}
                                </h5>
                              </div>

                              {/* İstersen satır altında birim fiyatı da gösterebilirsin */}
                              {/* <div className="text-end text-muted">
                                {unit ? formatMoney(unit.amount, unit.curr, {min:0,max:0}) : "-"} / {t("piece")}
                              </div> */}
                            </div>

                            <div className="row text-center">
                              <div className="col-lg-6 border-end">
                                <Link
                                  onClick={handleWishlistClick(item.id)}
                                  className="btn fw-14 mt-2 text-dark text-decoration-underline"
                                >
                                  <i
                                    className={`${
                                      wishlistItems.includes(item.id)
                                        ? "fa-solid fa-heart"
                                        : "fa-regular fa-heart"
                                    } fa-lg me-1 `}
                                  ></i>
                                  {t("AddtoWishlist")}
                                </Link>
                              </div>
                              <div className="col-lg-6">
                                <button
                                  className="btn fw-14 mt-2"
                                  onClick={() => removeItem(item)}
                                >
                                  <i className="fa-regular fa-circle-xmark"></i>{" "}
                                  {t("Remove")}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="row justify-content-center text-center">
                <div className="col-lg-10 bg-white p-5">
                  <img src={NotImageIcon} alt="deneme" className="w-50" />
                  <h4 className="fw-normal wbtextcolor">
                    {t("YourShoppingBagIsEmpty")}
                  </h4>
                  <Link
                    to="/shop"
                    className="btn bgcolorfooterbottom fw-11 p-2  pe-5 px-5 mt-2 fw-light"
                  >
                    {t("StartShopping")}
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-5">
            {cartItems.length > 0 ? (
              <>
                <div className="p-5">
                  <div className="row justify-content-center">
                    <div className="col-lg-9">
                      <div className="d-flex justify-content-between mb-3">
                        <h6 className="mb-0 fw-light">{t("Subtotal")}</h6>
                        <h6 className="mb-0 fw-normal">
                          {formatMoney(
                            getSubtotalObj.amount,
                            getSubtotalObj.curr,
                            { min: 0, max: 0 },
                          )}
                        </h6>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between mb-3">
                        <h6 className="mb-0 fw-light">{t("Shipping")}</h6>
                        <h6 className="mb-0 fw-normal">
                          {formatMoney(0, getTotalObj.curr, { min: 0, max: 0 })}
                        </h6>
                      </div>

                      <hr />
                      {getCustomizationTotalObj.amount > 0 && (
                        <>
                          <div className="d-flex justify-content-between mb-3">
                            <h6 className="mb-0 fw-light">
                              {t("customization.price")}
                            </h6>
                            <h6 className="mb-0 fw-normal">
                              {formatMoney(
                                getCustomizationTotalObj.amount,
                                getCustomizationTotalObj.curr,
                                { min: 0, max: 2 },
                              )}
                            </h6>
                          </div>
                          <hr />
                        </>
                      )}

                      <div className="d-flex justify-content-between mb-1">
                        <h6 className="mb-0 fw-light">{t("Total")}</h6>
                        <h6 className="mb-0 fw-normal">
                          {formatMoney(getTotalObj.amount, getTotalObj.curr, {
                            min: 0,
                            max: 0,
                          })}
                        </h6>
                      </div>
                      <Link
                        to="/checkout"
                        className="btn bgcolorfooterbottom w-100 mt-4 fw-lighter p-3 pt-3 pb-3"
                      >
                        {t("ProceedToCheckout")}
                      </Link>
                    </div>
                  </div>
                </div>
                <hr />
                <div className="py-1 mb-5">
                  <CartBottomMenu />
                </div>
              </>
            ) : (
              <div className="p-5">
                <CartBottomMenu />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Offcanvas (ürün detay özeti) */}
      <div
        className="offcanvas offcanvas-end w-50"
        tabIndex="-1"
        id="offcanvasRight"
        aria-labelledby="offcanvasRightLabel"
      >
        <div className="row justify-content-center mt-5">
          <div className="col-lg-7 pe-2 px-1">
            <div className="offcanvas-header">
              <h5 id="offcanvasRightLabel" className="fw-normal">
                {t("ViewDetails")}
              </h5>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>

        {selectedProduct && (
          <div className="offcanvas-body">
            <div className="row justify-content-center">
              <div className="col-lg-7">
                <div className="d-flex align-items-center">
                  <img
                    src={getColorImage(selectedProduct)}
                    alt={selectedProduct.name}
                    className="w-25"
                  />
                  <div className="ms-3">
                    <h6 className="mb-0 fs-25 fw-normal">
                      {selectedProduct.name}
                    </h6>
                    <p className="mb-0 fw-normal">
                      {(() => {
                        const u = getBaseUnitPriceObj(selectedProduct);
                        return u
                          ? formatMoney(u.amount, u.curr, { min: 0, max: 0 })
                          : "-";
                      })()}
                    </p>
                  </div>
                </div>
                <hr />
                <div className="mt-4">
                  <p className="mb-4">{selectedProduct.description}</p>

                  {!isPerfumeItem(selectedProduct) && (
                    <>
                      <p className="mb-0 fw-normal fs-11">
                        {selectedProduct?.attributes?.dimensions}
                      </p>
                      <p className="fw-normal fs-11">({t("ProductSize")})</p>
                    </>
                  )}
                  <div className="mb-3">
                    <p className="fw-normal fs-11 mb-1">
                      * {t("Category")}:{" "}
                      {Array.isArray(selectedProduct.category)
                        ? selectedProduct.category.join(", ")
                        : selectedProduct.category || "-"}
                    </p>
                    <p className="fw-normal fs-11 mb-1">
                      * {t("Color")}:{" "}
                      {getLocalizedColorName(selectedProduct, t)}
                    </p>
                    <p className="fw-normal fs-11 mb-1">
                      * {t("Tags")}: {selectedProduct.tags.join(", ")}
                    </p>
                    <p className="fw-normal fs-11 mb-1">
                      * {t("Weight")}:{" "}
                      {selectedProduct?.attributes?.weight ||
                        selectedProduct?.attributes?.dimensions}
                    </p>
                    <p className="fw-normal fs-11 mb-1">
                      * {t("Brand")}: {selectedProduct.attributes.brand}
                    </p>
                  </div>
                  <a
                    href={`/product/${selectedProduct.id}`}
                    className="btn bgcolorfooterbottom w-100 mt-4 p-3"
                  >
                    {t("ViewProductPage")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
